/**
 * Robot vacuum (module `vacuum`, server ha/vacuum.ts): Roborock entity ids, plain-English
 * states and errors, consumable wear, room-clean commands and run history derived from
 * the robot's `current_room` sensor. Pure, shared by server, web and tests.
 */
import { z } from 'zod';
// ---------------------------------------------------------------- entity ids
/** Every Roborock entity the module reads, from the vacuum entity's object id (`vacuum.saros_20` → `saros_20`). */
export function vacuumIds(vacuumEntity) {
    const p = vacuumEntity.replace(/^vacuum\./, '');
    return {
        vacuum: vacuumEntity,
        status: `sensor.${p}_status`,
        battery: `sensor.${p}_battery`,
        currentRoom: `sensor.${p}_current_room`,
        progress: `sensor.${p}_cleaning_progress`,
        area: `sensor.${p}_cleaning_area`,
        time: `sensor.${p}_cleaning_time`,
        begin: `sensor.${p}_last_clean_begin`,
        end: `sensor.${p}_last_clean_end`,
        error: `sensor.${p}_vacuum_error`,
        dockError: `sensor.${p}_dock_dock_error`,
        totalArea: `sensor.${p}_total_cleaning_area`,
        totalCount: `sensor.${p}_total_cleaning_count`,
        totalTime: `sensor.${p}_total_cleaning_time`,
        mainBrush: `sensor.${p}_main_brush_time_left`,
        sideBrush: `sensor.${p}_side_brush_time_left`,
        filter: `sensor.${p}_filter_time_left`,
        sensor: `sensor.${p}_sensor_time_left`,
        strainer: `sensor.${p}_dock_strainer_time_left`,
        mopDryingLeft: `sensor.${p}_dock_mop_drying_remaining_time`,
        cleaning: `binary_sensor.${p}_cleaning`,
        charging: `binary_sensor.${p}_charging`,
        mopAttached: `binary_sensor.${p}_mop_attached`,
        waterBox: `binary_sensor.${p}_water_box_attached`,
        waterShortage: `binary_sensor.${p}_water_shortage`,
        dirtyWater: `binary_sensor.${p}_dock_dirty_water_box`,
        cleanWater: `binary_sensor.${p}_dock_clean_water_box`,
        fluid: `binary_sensor.${p}_dock_cleaning_fluid`,
        mopDrying: `binary_sensor.${p}_dock_mop_drying`,
        mopMode: `select.${p}_mop_mode`,
        mopIntensity: `select.${p}_mop_intensity`,
        emptyMode: `select.${p}_dock_empty_mode`,
        selectedMap: `select.${p}_selected_map`,
        map: `image.${p}_map_0`,
    };
}
// ---------------------------------------------------------------- words
const humanize = (s) => {
    const t = s.replace(/_/g, ' ').trim();
    return t ? t[0].toUpperCase() + t.slice(1) : t;
};
const STATUS_TEXT = {
    starting: 'Starting', charger_disconnected: 'Off the dock', idle: 'Idle', remote_control_active: 'Remote control',
    cleaning: 'Cleaning', returning_home: 'Returning to the dock', manual_mode: 'Manual mode', charging: 'Charging',
    charging_problem: 'Charging problem', paused: 'Paused', spot_cleaning: 'Spot cleaning', error: 'Error',
    shutting_down: 'Shutting down', updating: 'Updating', docking: 'Docking', going_to_target: 'Going to a spot',
    zoned_cleaning: 'Cleaning a zone', segment_cleaning: 'Cleaning rooms', emptying_the_bin: 'Emptying the bin',
    washing_the_mop: 'Washing the mop', washing_the_mop_2: 'Washing the mop', going_to_wash_the_mop: 'Going to wash the mop',
    in_call: 'In a call', mapping: 'Mapping', patrol: 'Patrolling', attaching_the_mop: 'Attaching the mop',
    detaching_the_mop: 'Detaching the mop', charging_complete: 'Charged', device_offline: 'Offline', locked: 'Locked',
    air_drying_stopping: 'Drying the mop', robot_status_mopping: 'Mopping', docked: 'Docked', returning: 'Returning to the dock',
    unavailable: 'Unavailable', unknown: 'Unknown',
};
export const vacuumStatusText = (s) => (s ? STATUS_TEXT[s] ?? humanize(s) : '—');
/** Statuses during which the robot is out doing a job (rooms visited count as cleaned). */
export const BUSY_STATUSES = new Set([
    'cleaning', 'segment_cleaning', 'zoned_cleaning', 'spot_cleaning', 'robot_status_mopping', 'going_to_wash_the_mop',
    'washing_the_mop', 'washing_the_mop_2', 'returning_home', 'going_to_target', 'paused',
]);
const DOCK_ERROR_TEXT = {
    ok: 'No problems',
    no_dustbin_or_filter: 'The dock has no dust bag or filter fitted',
    auto_empty_dock_fan_error: 'The dock’s emptying fan has a fault',
    duct_blockage: 'The dock’s dust duct is blocked',
    auto_empty_dock_voltage_error: 'The dock has a power fault',
    water_empty: 'Clean water tank is empty',
    waste_water_tank_full: 'Dirty water tank is full',
    maintenance_brush_jammed: 'The dock’s cleaning brush is jammed',
    dirty_tank_latch_open: 'The dirty water tank latch is open',
    no_dustbin: 'The dock has no dust bin',
    cleaning_tank_full_or_blocked: 'The dock’s wash tray is full or blocked',
};
/** Plain-English dock error; null when there is none. */
export function dockErrorText(code) {
    if (!code || code === 'ok' || code === 'none' || code === 'unknown' || code === 'unavailable')
        return null;
    return DOCK_ERROR_TEXT[code] ?? humanize(code);
}
/** What to do about a dock error. */
export function dockErrorFix(code) {
    switch (code) {
        case 'water_empty': return 'Fill the clean water tank and put it back in the dock.';
        case 'waste_water_tank_full': return 'Empty and rinse the dirty water tank.';
        case 'no_dustbin_or_filter':
        case 'no_dustbin': return 'Fit a dust bag and close the dock lid.';
        case 'duct_blockage': return 'Clear the dust duct between the robot and the dock.';
        case 'dirty_tank_latch_open': return 'Push the dirty water tank in until the latch clicks.';
        case 'cleaning_tank_full_or_blocked': return 'Clean the wash tray and its filter.';
        case 'maintenance_brush_jammed': return 'Free the brush in the dock’s wash tray.';
        default: return null;
    }
}
/** Dock errors that stop mopping (the robot can still vacuum). */
export const MOP_BLOCKING_DOCK_ERRORS = new Set(['water_empty', 'waste_water_tank_full', 'dirty_tank_latch_open', 'cleaning_tank_full_or_blocked', 'maintenance_brush_jammed']);
const VACUUM_ERROR_TEXT = {
    none: 'No problems',
    lidar_blocked: 'The laser sensor is blocked', bumper_stuck: 'The bumper is stuck', wheels_suspended: 'A wheel is off the floor',
    cliff_sensor_error: 'The cliff sensors are dirty', main_brush_jammed: 'The main brush is jammed', side_brush_jammed: 'The side brush is jammed',
    wheels_jammed: 'A wheel is jammed', robot_trapped: 'The robot is stuck', no_dustbin: 'The dust bin is missing',
    strainer_error: 'The filter is wet or blocked', compass_error: 'Magnetic interference', low_battery: 'Battery too low to clean',
    charging_error: 'It can’t charge', battery_error: 'Battery fault', wall_sensor_dirty: 'The wall sensor is dirty',
    robot_tilted: 'The robot is tilted', side_brush_error: 'Side brush fault', fan_error: 'Suction fan fault', dock: 'Dock problem',
    optical_flow_sensor_dirt: 'The floor sensor is dirty', vertical_bumper_pressed: 'The top bumper is pressed',
    dock_locator_error: 'It can’t find the dock', return_to_dock_fail: 'It couldn’t get back to the dock',
    nogo_zone_detected: 'It’s in a no-go zone', visual_sensor: 'The camera is dirty or blocked', light_touch: 'The light sensor is blocked',
    vibrarise_jammed: 'The mop lift is jammed', robot_on_carpet: 'It’s stuck on a carpet', filter_blocked: 'The filter is blocked',
    invisible_wall_detected: 'It hit a virtual wall', cannot_cross_carpet: 'It can’t cross a carpet', internal_error: 'Internal error',
};
/** Plain-English robot error; null when there is none. */
export function vacuumErrorText(code) {
    if (!code || code === 'none' || code === 'unknown' || code === 'unavailable')
        return null;
    return VACUUM_ERROR_TEXT[code] ?? humanize(code);
}
export const FAN_SPEED_TEXT = {
    quiet: 'Quiet', balanced: 'Balanced', turbo: 'Turbo', max: 'Max', max_plus: 'Max+', off_raise_main_brush: 'Off (mop only)',
    smart_mode: 'Smart', custom: 'Custom (app)',
};
export const MOP_INTENSITY_TEXT = {
    off: 'Off', slight: 'Slight', low: 'Low', medium: 'Medium', moderate: 'Moderate', high: 'High', extreme: 'Extreme',
};
export const MOP_MODE_TEXT = {
    standard: 'Standard', deep: 'Deep', deep_plus: 'Deep+', fast: 'Fast', smart_mode: 'Smart', custom: 'Custom (app)',
};
export const EMPTY_MODE_TEXT = { smart: 'Smart', light: 'Light', balanced: 'Balanced', max: 'Max', unknown: 'Unknown' };
/** Roborock replacement / cleaning intervals in hours of cleaning. */
export const CONSUMABLES = [
    { key: 'mainBrush', label: 'Main brush', lifeH: 300, action: 'Replace' },
    { key: 'sideBrush', label: 'Side brush', lifeH: 200, action: 'Replace' },
    { key: 'filter', label: 'Filter', lifeH: 150, action: 'Replace' },
    { key: 'sensor', label: 'Sensors', lifeH: 30, action: 'Clean' },
    { key: 'strainer', label: 'Dock strainer', lifeH: 150, action: 'Clean' },
];
/** Hours left → wear state. Negative = overdue; under 10 % of the interval (or 5 h) = soon. */
export function consumableState(hoursLeft, lifeH) {
    if (hoursLeft < 0)
        return 'overdue';
    if (hoursLeft <= Math.max(5, lifeH * 0.1))
        return 'soon';
    return 'ok';
}
export function consumables(read) {
    const out = [];
    for (const c of CONSUMABLES) {
        const h = read(c.key);
        if (h === undefined || !Number.isFinite(h))
            continue;
        out.push({ ...c, hoursLeft: h, pct: Math.max(0, Math.min(100, (h / c.lifeH) * 100)), state: consumableState(h, c.lifeH) });
    }
    return out;
}
// ---------------------------------------------------------------- room mapping & commands
/** App room → the robot's room name (its `current_room` option) and Roborock segment id(s). */
export const VacuumRoomMap = z.object({
    roomId: z.string().min(1).max(64),
    robotRoom: z.string().min(1).max(64),
    segments: z.array(z.number().int().min(1).max(1_000_000)).max(8),
});
export const VacuumPreset = z.object({
    id: z.string().min(1).max(40).regex(/^[a-z0-9-]+$/),
    name: z.string().min(1).max(40),
    rooms: z.array(z.string().min(1)).min(1).max(16),
});
/** Admin settings (PUT /api/admin/homes/:home/vacuum). */
export const VacuumSettings = z.object({
    entityId: z.string().regex(/^vacuum\.[a-z0-9_]+$/),
    rooms: z.array(VacuumRoomMap).max(32),
    presets: z.array(VacuumPreset).max(12),
    /** The house's cleaning schedule automation (shown on the dashboard), and the alarm rule that ignores PIRs while it cleans. */
    scheduleAutomation: z.string().regex(/^automation\.[a-z0-9_]+$/).nullable(),
    alarmAutomation: z.string().regex(/^automation\.[a-z0-9_]+$/).nullable(),
}).superRefine((s, ctx) => {
    const seen = new Set();
    for (const r of s.rooms) {
        if (seen.has(r.roomId))
            ctx.addIssue({ code: 'custom', message: `${r.roomId} is mapped twice` });
        seen.add(r.roomId);
    }
});
export const FanSpeed = z.enum(['quiet', 'balanced', 'turbo', 'max', 'max_plus', 'off_raise_main_brush', 'smart_mode']);
export const MopMode = z.enum(['standard', 'deep', 'deep_plus', 'fast', 'smart_mode']);
export const MopIntensity = z.enum(['off', 'slight', 'low', 'medium', 'moderate', 'high', 'extreme']);
/** Semantic room clean (POST /api/homes/:home/vacuum/clean → gateway `vacuum.clean_rooms`). Rooms in the order to clean them. */
export const VacuumCleanBody = z.object({
    rooms: z.array(z.string().min(1).max(64)).min(1).max(16),
    repeat: z.number().int().min(1).max(3).default(1),
    fanSpeed: FanSpeed.optional(),
    mopMode: MopMode.optional(),
    mopIntensity: MopIntensity.optional(),
});
/** Robot commands. wash_mop / dry_mop are dock jobs (Roborock app_start_wash / app_set_dryer_status) and need the robot docked. */
export const VacuumCommand = z.enum(['start', 'pause', 'stop', 'return_to_base', 'locate', 'wash_mop', 'dry_mop']);
export class SegmentError extends Error {
    rooms;
    constructor(message, rooms) {
        super(message);
        this.rooms = rooms;
        this.name = 'SegmentError';
    }
}
/**
 * Ordered, de-duplicated segment ids for app rooms (in the order given). Throws SegmentError naming
 * the rooms that aren't mapped or have no segment id yet.
 */
export function segmentsFor(roomIds, mapping) {
    const by = new Map(mapping.map((m) => [m.roomId, m]));
    const missing = roomIds.filter((id) => !by.get(id)?.segments.length);
    if (missing.length)
        throw new SegmentError(`No Roborock segment set for ${missing.join(', ')}`, missing);
    const out = [];
    for (const id of roomIds)
        for (const s of by.get(id).segments)
            if (!out.includes(s))
                out.push(s);
    return out;
}
/** `vacuum.send_command` data for a Roborock room clean (HA roborock docs: app_segment_clean). */
export function segmentCleanCommand(segments, repeat) {
    return { command: 'app_segment_clean', params: [{ segments: [...segments], repeat: Math.max(1, Math.min(3, Math.round(repeat))) }] };
}
/** `vacuum.send_command` data for the dock jobs (python-roborock RoborockCommand.APP_START_WASH / APP_SET_DRYER_STATUS). */
export function dockJobCommand(job) {
    return job === 'wash_mop' ? { command: 'app_start_wash' } : { command: 'app_set_dryer_status', params: { status: 1 } };
}
/** Every send_command the server may issue to the robot. */
export const ROBOT_COMMANDS = new Set(['app_segment_clean', 'app_start_wash', 'app_set_dryer_status']);
/** Robot states in which it's on the dock (wash / dry allowed). */
export function isDocked(state, status) {
    return state === 'docked' || status === 'charging' || status === 'charging_complete' || status === 'emptying_the_bin' || status === 'air_drying_stopping';
}
// ---------------------------------------------------------------- routines
export const RoutineMode = z.enum(['vacuum', 'vacuum_mop', 'mop']);
export const RoutineSchedule = z.object({
    /** 0 = Monday … 6 = Sunday, in the home's timezone. */
    days: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    enabled: z.boolean(),
});
/** A saved cleaning routine: rooms in order plus how to clean them. */
export const VacuumRoutineBody = z.object({
    name: z.string().trim().min(1).max(40),
    rooms: z.array(z.string().min(1).max(64)).min(1).max(16).refine((r) => new Set(r).size === r.length, 'A room is listed twice'),
    repeat: z.number().int().min(1).max(3),
    fanSpeed: FanSpeed,
    mode: RoutineMode,
    mopMode: MopMode.optional(),
    mopIntensity: MopIntensity.optional(),
    schedule: RoutineSchedule.nullable(),
});
/** The clean body a routine sends: rooms in the stored order, then suction and mop for its mode. */
export function routineClean(r) {
    const base = { rooms: [...r.rooms], repeat: r.repeat };
    switch (r.mode) {
        case 'vacuum': return { ...base, fanSpeed: r.fanSpeed, mopIntensity: 'off' };
        case 'vacuum_mop': return { ...base, fanSpeed: r.fanSpeed, mopMode: r.mopMode ?? 'standard', mopIntensity: r.mopIntensity && r.mopIntensity !== 'off' ? r.mopIntensity : 'medium' };
        case 'mop': return { ...base, fanSpeed: 'off_raise_main_brush', mopMode: r.mopMode ?? 'standard', mopIntensity: r.mopIntensity && r.mopIntensity !== 'off' ? r.mopIntensity : 'medium' };
    }
}
const WEEKDAY = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
/** Local weekday (0 = Mon), date and HH:MM at `t` in `timeZone`. */
export function localClock(t, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(t * 1000);
    const g = (k) => parts.find((p) => p.type === k)?.value ?? '';
    const hm = `${g('hour')}:${g('minute')}`;
    return { dow: WEEKDAY[g('weekday')] ?? 0, day: `${g('year')}-${g('month')}-${g('day')}`, hm, minutes: Number(g('hour')) * 60 + Number(g('minute')) };
}
/** A scheduled routine is due: today is one of its days, its time passed within `graceMin` minutes, and it hasn't run today. */
export function routineDue(s, lastRunDay, t, timeZone, graceMin = 10) {
    if (!s?.enabled)
        return false;
    const c = localClock(t, timeZone);
    if (!s.days.includes(c.dow) || lastRunDay === c.day)
        return false;
    const [h, m] = s.time.split(':').map(Number);
    const d = c.minutes - (h * 60 + m);
    return d >= 0 && d <= graceMin;
}
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** "Every day at 05:00", "Weekdays at 09:30", "Mon, Thu at 10:00". */
export function scheduleText(s) {
    const d = [...s.days].sort();
    const days = d.length === 7 ? 'Every day' : d.join() === '0,1,2,3,4' ? 'Weekdays' : d.join() === '5,6' ? 'Weekends' : d.map((x) => DAY_SHORT[x]).join(', ');
    return `${days} at ${s.time}`;
}
/**
 * Dock and robot supplies from the Roborock entities. The dock binary_sensors are device_class `problem`
 * (on = needs attention: on with dock_error water_empty live on 5 Oct 2026); mop_attached is `connectivity`
 * (on = fitted). There's no dust-bag sensor on the Saros: the bag shows only through dock errors.
 */
export function supplies(read, ids) {
    const on = (id) => {
        const s = read(id);
        return s === 'on' ? true : s === 'off' ? false : null;
    };
    const dock = read(ids.dockError) ?? null;
    const out = [];
    const clean = on(ids.cleanWater);
    const shortage = on(ids.waterShortage);
    const cleanEmpty = clean === true || shortage === true || dock === 'water_empty';
    if (clean !== null || shortage !== null || dock !== null) {
        out.push({ key: 'cleanWater', label: 'Clean water', state: cleanEmpty ? 'refill' : 'ok', word: cleanEmpty ? 'Refill' : 'OK', fix: cleanEmpty ? 'Refill the clean water tank' : null, blocks: cleanEmpty ? 'mop' : null });
    }
    const dirty = on(ids.dirtyWater);
    const dirtyFull = dirty === true || dock === 'waste_water_tank_full' || dock === 'dirty_tank_latch_open';
    if (dirty !== null || dirtyFull) {
        out.push({ key: 'dirtyWater', label: 'Dirty water', state: dirtyFull ? 'empty' : 'ok', word: dirtyFull ? 'Empty' : 'OK', fix: dirtyFull ? (dock === 'dirty_tank_latch_open' ? 'Push the dirty water tank in until it clicks' : 'Empty the dirty water tank') : null, blocks: dirtyFull ? 'mop' : null });
    }
    const fluid = on(ids.fluid);
    if (fluid !== null)
        out.push({ key: 'fluid', label: 'Cleaning solution', state: fluid ? 'topup' : 'ok', word: fluid ? 'Top up' : 'OK', fix: fluid ? 'Top up the cleaning solution' : null, blocks: null });
    if (dock === 'no_dustbin_or_filter' || dock === 'no_dustbin' || dock === 'duct_blockage') {
        out.push({ key: 'dustBag', label: 'Dust bag', state: 'empty', word: dock === 'duct_blockage' ? 'Blocked' : 'Missing', fix: dock === 'duct_blockage' ? 'Clear the dust duct' : 'Fit a dust bag', blocks: 'all' });
    }
    const mop = on(ids.mopAttached);
    if (mop !== null)
        out.push({ key: 'mop', label: 'Mop pads', state: mop ? 'ok' : 'attach', word: mop ? 'OK' : 'Attach', fix: mop ? null : 'Attach the mop pads', blocks: mop ? null : 'mop' });
    return out;
}
/** The banner line: what blocks cleaning or mopping now, or null. */
export function supplyBanner(list) {
    const bad = list.filter((s) => s.blocks);
    if (!bad.length)
        return null;
    const all = bad.some((s) => s.blocks === 'all');
    return { blocks: all ? 'all' : 'mop', text: all ? "The robot can't clean until this is sorted" : 'It can vacuum but not mop until this is sorted', fixes: bad.map((s) => s.fix).filter(Boolean) };
}
/** Shorter stays are passing through (e.g. the hall on the way out of the dock). */
export const MIN_ROOM_SECONDS = 60;
const BAD = new Set(['unavailable', 'unknown', '']);
/**
 * Runs from HA history: `cleaning` = binary_sensor.<p>_cleaning rows (on/off), `room` = current_room rows,
 * `area` = cleaning_area rows. Rows are [epoch s, state], any order. A run is an on→off interval; its rooms come
 * from current_room clipped to the run; its area is the last area reading up to 15 min after it ends.
 */
export function deriveRuns(cleaning, room, area, now, minRoomSeconds = MIN_ROOM_SECONDS) {
    const c = [...cleaning].filter((r) => !BAD.has(r[1])).sort((a, b) => a[0] - b[0]);
    const rm = [...room].sort((a, b) => a[0] - b[0]);
    const ar = [...area].filter((r) => !BAD.has(r[1]) && Number.isFinite(Number(r[1]))).sort((a, b) => a[0] - b[0]);
    const spans = [];
    let open = null;
    for (const [t, s] of c) {
        if (s === 'on' && open === null)
            open = t;
        else if (s === 'off' && open !== null) {
            spans.push([open, t]);
            open = null;
        }
    }
    if (open !== null)
        spans.push([open, null]);
    return spans.map(([start, end]) => {
        const stop = end ?? now;
        const visits = new Map();
        for (let i = 0; i < rm.length; i++) {
            const [t, name] = rm[i];
            const next = rm[i + 1]?.[0] ?? Infinity;
            const a = Math.max(t, start);
            const b = Math.min(next, stop);
            if (b <= a || BAD.has(name))
                continue;
            const v = visits.get(name) ?? { robotRoom: name, seconds: 0, firstAt: a, lastAt: b, settled: false };
            v.seconds += b - a;
            // Ordered by the first stay long enough to be cleaning, not by passing through on the way.
            if (!v.settled && b - a >= minRoomSeconds) {
                v.firstAt = a;
                v.settled = true;
            }
            v.lastAt = Math.max(v.lastAt, b);
            visits.set(name, v);
        }
        const rooms = [...visits.values()].filter((v) => v.seconds >= minRoomSeconds).sort((x, y) => x.firstAt - y.firstAt)
            .map(({ settled: _s, ...v }) => ({ ...v, seconds: Math.round(v.seconds) }));
        let areaM2 = null;
        for (const [t, s] of ar) {
            if (t < start)
                continue;
            if (t > stop + 900)
                break;
            areaM2 = Number(s);
        }
        return { start, end, seconds: Math.round(stop - start), areaM2, rooms };
    });
}
/** Robot room name → when it was last cleaned (end of its last qualifying visit). */
export function lastCleaned(runs) {
    const out = new Map();
    for (const r of runs)
        for (const v of r.rooms)
            out.set(v.robotRoom, Math.max(out.get(v.robotRoom) ?? 0, v.lastAt));
    return out;
}
/** Area per local day (YYYY-MM-DD in `timeZone`) for the last `days` days ending today, oldest first. */
export function areaByDay(runs, now, timeZone, days = 7) {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    const dayOf = (t) => fmt.format(t * 1000);
    const out = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = dayOf(now - i * 86400);
        if (!out.some((x) => x.day === d))
            out.push({ day: d, areaM2: 0, runs: 0 });
    }
    for (const r of runs) {
        const slot = out.find((x) => x.day === dayOf(r.start));
        if (!slot)
            continue;
        slot.runs += 1;
        slot.areaM2 += r.areaM2 ?? 0;
    }
    return out.map((x) => ({ ...x, areaM2: Math.round(x.areaM2 * 10) / 10 }));
}
/** Area per week (Monday start, local) for the last `weeks` weeks, oldest first. `day` is the Monday. */
export function areaByWeek(runs, now, timeZone, weeks = 8) {
    const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    const wd = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short' });
    const DOW = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
    const mondayOf = (t) => fmt.format((t - (DOW[wd.format(t * 1000)] ?? 0) * 86400) * 1000);
    const out = [];
    for (let i = weeks - 1; i >= 0; i--) {
        const d = mondayOf(now - i * 7 * 86400);
        if (!out.some((x) => x.day === d))
            out.push({ day: d, areaM2: 0, runs: 0 });
    }
    for (const r of runs) {
        const slot = out.find((x) => x.day === mondayOf(r.start));
        if (!slot)
            continue;
        slot.runs += 1;
        slot.areaM2 += r.areaM2 ?? 0;
    }
    return out.map((x) => ({ ...x, areaM2: Math.round(x.areaM2 * 10) / 10 }));
}
//# sourceMappingURL=vacuum.js.map